/**
 * How people are drawn: glass and a colour ring, the opposite of an amenity, which is colour inside and white outside
 * (Sam picked `ring` on 2026-11-01). Dev builds can still compare: `?face=ring|halo|glow|today`, remembered
 * across pages.
 *   ring  dark purple glass, the person's colour as the ring, white initial (the look)
 *   halo  ring, with a thin white line outside it (reads on a bright map)
 *   glow  ring, glowing in the person's colour
 * A photo sits inside the same ring. Your own token is not touched (it stays plain purple).
 *
 * Inside the ring: a flat wash of the person's color over the dark ground (Sam's pick D, 2026-11-01), no shading and no
 * top light; it replaced the purple glass gradient.
 */

import { useSyncExternalStore } from 'react';

const KEY = 'prii.dev.face';
export const FACE_STYLES = ['today', 'ring', 'halo', 'glow'];

/** The ring colours: the identity hues, softened toward the brand (one per AVATAR_COLORS slot). */
export const RING_HUES = [
  '#8b5cf6',
  '#d946ef',
  '#f472b6',
  '#fb7185',
  '#f59e0b',
  '#a3c45a',
  '#2dd4bf',
  '#38bdf8',
  '#818cf8',
  '#c084fc',
];

let cached = null;
export function faceStyle() {
  if (cached) return cached;
  cached = 'ring';
  if (!import.meta.env.DEV || typeof window === 'undefined') return cached;
  try {
    const q = new URLSearchParams(window.location.search).get('face');
    if (q && FACE_STYLES.includes(q)) {
      if (q === 'ring') window.localStorage.removeItem(KEY);
      else window.localStorage.setItem(KEY, q);
      cached = q;
    } else cached = window.localStorage.getItem(KEY) || 'ring';
  } catch {
    /* storage off: the default */
  }
  if (!FACE_STYLES.includes(cached)) cached = 'ring';
  return cached;
}
/**
 * How a selected or tracked friend is marked: b, a glow in their own colour (Sam's pick, 2026-11-01), like your profile
 * photo and your corner avatar. Dev builds can still compare with `?psel=`: a = the old look (Maps: the pin only grew;
 * Find: a thin purple outline / ground ring), c = the selected amenity's white halo, d = both. Remembered across pages.
 */
export const SEL_STYLES = ['a', 'b', 'c', 'd'];
let selCached = null;
export function selStyle() {
  if (selCached) return selCached;
  selCached = 'b';
  if (!import.meta.env.DEV || typeof window === 'undefined') return selCached;
  try {
    const q = new URLSearchParams(window.location.search).get('psel');
    if (q && SEL_STYLES.includes(q)) {
      if (q === 'b') window.localStorage.removeItem('prii.dev.psel');
      else window.localStorage.setItem('prii.dev.psel', q);
      selCached = q;
    } else selCached = window.localStorage.getItem('prii.dev.psel') || 'b';
  } catch {
    /* storage off: the default */
  }
  if (!SEL_STYLES.includes(selCached)) selCached = 'b';
  return selCached;
}

/**
 * The name over a friend you selected on Maps, like a selected amenity's: g = the count chip's dark glass with an edge in
 * their colour (Sam's pick, 2026-11-01). Dev builds can still compare with `?plabel=`: 1 = a pill in their colour,
 * 3 = their name under the spot. Read on each call, so a dev hook can flip it without a reload (window.__plabel).
 */
export const LABEL_STYLES = ['1', 'g', '3'];
export function labelStyle() {
  if (!import.meta.env.DEV || typeof window === 'undefined') return 'g';
  const q =
    /** @type {any} */ (window).__plabel ??
    new URLSearchParams(window.location.search).get('plabel');
  return LABEL_STYLES.includes(q) ? q : 'g';
}

/** false only under `?face=today` (the old solid faces, kept to compare against) */
export const glassFaces = () => faceStyle() !== 'today';

// Avatar.js's identity hash (same slot, so a friend's ring is their list colour, softened). Not imported: Avatar
// reads this file, and the build refuses a cycle.
function slot(name) {
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash << 5) - hash + name.charCodeAt(i);
  return Math.abs(hash) % RING_HUES.length;
}

// Picked colours (Edit Profile, migration 173): display name -> slot, for you and the people you know
// (get_people_hues). Faces are drawn by name everywhere, so they are keyed by name; anyone not here keeps the hash.
// Kept on the phone so a cold open with no service still has them.
const CHOSEN_KEY = 'prii.hues';
let chosen = new Map();
try {
  if (typeof window !== 'undefined')
    chosen = new Map(JSON.parse(window.localStorage.getItem(CHOSEN_KEY) || '[]'));
} catch {
  /* storage off or bad JSON: none picked */
}
let huesVersion = 0;
const hueSubs = new Set();
function hueChanged() {
  huesVersion++;
  try {
    window.localStorage.setItem(CHOSEN_KEY, JSON.stringify([...chosen]));
  } catch {
    /* storage off: this session only */
  }
  for (const fn of hueSubs) fn();
}
/** rows: [{ display_name, avatar_hue }] from get_people_hues - the whole set, replacing what was there. */
export function setChosenHues(rows) {
  const next = new Map();
  for (const r of rows || [])
    if (r?.display_name?.trim() && Number.isInteger(r.avatar_hue) && RING_HUES[r.avatar_hue])
      next.set(r.display_name.trim(), r.avatar_hue);
  if (next.size === chosen.size && [...next].every(([k, v]) => chosen.get(k) === v)) return;
  chosen = next;
  hueChanged();
}
/**
 * One person's colour as read from their profile row: your own pick at once (Edit Profile), or someone's profile you
 * opened who is not in get_people_hues. null = not picked, back to the hash.
 */
export function noteHue(displayName, hue) {
  const name = displayName?.trim();
  if (!name) return;
  const h = Number.isInteger(hue) && RING_HUES[hue] ? hue : null;
  if ((chosen.get(name) ?? null) === h) return;
  if (h == null) chosen.delete(name);
  else chosen.set(name, h);
  hueChanged();
}
/** For useSyncExternalStore: faces re-render when a colour arrives. */
export function subscribeHues(fn) {
  hueSubs.add(fn);
  return () => hueSubs.delete(fn);
}
export const getHuesVersion = () => huesVersion;
/** Re-renders a component when a picked colour arrives or changes. */
export function useHues() {
  return useSyncExternalStore(subscribeHues, getHuesVersion, getHuesVersion);
}
/** The slot a name shows in: picked, else the hash. */
export function hueSlot(displayName) {
  if (!displayName) return 0;
  const name = String(displayName).trim();
  return chosen.has(name) ? chosen.get(name) : slot(displayName);
}

export function ringColor(displayName, cluster = false) {
  if (cluster || !displayName) return '#c89bff';
  return RING_HUES[hueSlot(displayName)];
}

const WASH_GROUND = [20, 11, 32]; // #140b20
/** The face's fill: 28% of the ring color over the dark ground, flat. */
export function washColor(ring) {
  const n = parseInt(String(ring).slice(1), 16);
  const c = [n >> 16, (n >> 8) & 255, n & 255];
  return `#${c
    .map((v, i) =>
      Math.round(v * 0.28 + WASH_GROUND[i] * 0.72)
        .toString(16)
        .padStart(2, '0'),
    )
    .join('')}`;
}

/** The glass behind a face, as CSS (list avatars). */
export function glassCss(displayName) {
  const c = ringColor(displayName);
  return {
    backgroundColor: washColor(c),
    boxShadow:
      `inset 0 0 0 2px ${c}` +
      (faceStyle() === 'halo' ? ', 0 0 0 1px rgb(255 255 255 / 0.85)' : '') +
      (faceStyle() === 'glow' ? `, 0 0 10px ${c}aa` : ''),
    color: '#fff',
  };
}

/**
 * Paints a glass face of radius r at (cx, cy). `inPin`: the pin's own head already draws the ring, so only the glass
 * and the initial (or photo) are painted.
 */
export function paintGlassFace(
  ctx,
  cx,
  cy,
  r,
  { displayName, image, cluster, initial, inPin = false },
) {
  const style = faceStyle(),
    c = ringColor(displayName, cluster),
    ring = Math.max(1.5, r * 0.13);
  const inner = inPin ? r : r - ring;
  ctx.save();
  if (!inPin) {
    if (style === 'glow') {
      ctx.shadowColor = c;
      ctx.shadowBlur = r * 0.6;
    }
    if (style === 'halo') {
      ctx.beginPath();
      ctx.arc(cx, cy, r + Math.max(1, r * 0.06), 0, Math.PI * 2);
      ctx.fillStyle = 'rgba(255,255,255,.9)';
      ctx.fill();
    }
    ctx.beginPath();
    ctx.arc(cx, cy, r, 0, Math.PI * 2);
    ctx.fillStyle = c;
    ctx.fill();
    ctx.shadowBlur = 0;
  }
  ctx.beginPath();
  ctx.arc(cx, cy, inner, 0, Math.PI * 2);
  ctx.fillStyle = washColor(c);
  ctx.fill();
  if (image) {
    ctx.clip();
    const s = Math.max((2 * inner) / image.width, (2 * inner) / image.height);
    ctx.drawImage(
      image,
      cx - (image.width * s) / 2,
      cy - (image.height * s) / 2,
      image.width * s,
      image.height * s,
    );
  } else if (!cluster && initial) {
    ctx.fillStyle = '#fff';
    ctx.font = `700 ${inner * 0.95}px Inter, system-ui, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    ctx.fillText(initial, cx, cy + inner * 0.04);
  }
  ctx.restore();
}

/**
 * The same face as SVG markup (the 2D map's markers, the 3D map's group pop-up): the wash fill and the ring as a
 * stroke laid inside radius r. `gid` is kept for callers; nothing on the page is named by it any more.
 */
// eslint-disable-next-line no-unused-vars -- gid: see above
export function glassFaceSvg({ cx, cy, r, displayName, cluster = false, gid }) {
  const c = ringColor(displayName, cluster),
    w = Math.max(1.5, r * 0.13);
  const glow = faceStyle() === 'glow' ? ` style="filter:drop-shadow(0 0 ${r * 0.3}px ${c})"` : '';
  const halo =
    faceStyle() === 'halo'
      ? `<circle cx="${cx}" cy="${cy}" r="${r + 0.6}" fill="none" stroke="#fff" stroke-opacity="0.9" stroke-width="1.2"/>`
      : '';
  return (
    halo +
    `<circle cx="${cx}" cy="${cy}" r="${r - w / 2}" fill="${washColor(c)}" stroke="${c}" stroke-width="${w}"${glow}/>`
  );
}

/**
 * You as a bubble pin: lavender glass on a short lavender nub (Sam's picks, 2026-11-02: D, then bubble 2 + stand n). Dev
 * builds can still compare with `?self=<bubble><stand>` (`?self=1n`, `?self=8t`; SELF_BUBBLES / SELF_STANDS in
 * lib/map3d/pinDraft.js) or `?self=puck` (the old token), remembered across pages; `?self=0` forgets, and the dev picker
 * (SelfLookPicker) flips it live. Null = the puck.
 */
const SELF_KEY = 'prii.dev.self';
const SELF_PICK = '2n';
const selfSubs = new Set();
let selfCached;
function parseSelf(v) {
  if (v === 'puck') return null;
  const m = /^(10|[1-9])([nwtdshfb])$/.exec(String(v || ''));
  return m ? { bubble: Number(m[1]), stand: m[2], key: m[0] } : parseSelf(SELF_PICK);
}
export function selfLook() {
  if (selfCached !== undefined) return selfCached;
  selfCached = parseSelf(SELF_PICK);
  if (!import.meta.env.DEV || typeof window === 'undefined') return selfCached;
  try {
    const q = new URLSearchParams(window.location.search).get('self');
    if (q === '0') window.localStorage.removeItem(SELF_KEY);
    else if (q) window.localStorage.setItem(SELF_KEY, q);
    selfCached = parseSelf(window.localStorage.getItem(SELF_KEY));
  } catch {
    /* storage off: the pick */
  }
  return selfCached;
}
/** Dev: switch your look live ('puck' = the old token, null = the pick). */
export function setSelfLook(key) {
  selfCached = parseSelf(key);
  try {
    if (key) window.localStorage.setItem(SELF_KEY, key);
    else window.localStorage.removeItem(SELF_KEY);
  } catch {
    /* storage off: this page only */
  }
  for (const fn of selfSubs) fn();
}
export function subscribeSelfLook(fn) {
  selfSubs.add(fn);
  return () => selfSubs.delete(fn);
}
/** Re-renders a component when your look changes (dev picker). */
export function useSelfLook() {
  return useSyncExternalStore(subscribeSelfLook, selfLook, selfLook);
}

/**
 * How the guide arrow meets you on a hill: d = from your feet, following the ground the whole way (Sam's pick,
 * 2026-11-02). Dev builds can still compare (`?arrow=` or window.__arrow): a = the old one flat board tilted to the slope
 * at your spot, starting past your token's edge; f = that board starting at your feet. Read each frame.
 */
export const ARROW_RULES = ['a', 'f', 'd'];
export function arrowRule() {
  if (!import.meta.env.DEV || typeof window === 'undefined') return 'd';
  const q =
    /** @type {any} */ (window).__arrow ?? new URLSearchParams(window.location.search).get('arrow');
  return ARROW_RULES.includes(q) ? q : 'd';
}
/**
 * How your bubble keeps clear of the arrow while you navigate: b, see-through (Sam's pick, 2026-11-02). In the low walking
 * view the bubble, at the puck's ground size, stands over the arrow. Dev compares the others (`?navself=` or
 * window.__navSelf): off = solid; a = a selected friend's size; c = the arrow drawn over you; d = leaning back like the
 * puck; e = halfway between the puck's size and a friend's.
 */
export const NAV_SELF_RULES = ['off', 'a', 'b', 'c', 'd', 'e'];
export function navSelfRule() {
  if (!import.meta.env?.DEV || typeof window === 'undefined') return 'b';
  const q =
    /** @type {any} */ (window).__navSelf ??
    new URLSearchParams(window.location.search).get('navself');
  return NAV_SELF_RULES.includes(q) ? q : 'b';
}
if (import.meta.env?.DEV && typeof window !== 'undefined')
  /** @type {any} */ (window).__setSelfLook = setSelfLook; // for render scripts
